import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertStandaloneProps, NAlertStandalone } from '../Nemo/NAlertStandalone/NAlertStandalone';
import { useMediaQuery } from '@mui/system';
import { theme } from '../../styles/theme';

export interface OdysseyFloatingAlertProps extends AlertStandaloneProps {
  floating?: boolean;
  position?:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'bottom-center'
    | 'top-center';
  autoHide?: boolean;
  autoHideDuration?: number;
  isDesktop?: boolean; // Optional prop to indicate if it's in desktop context
}

export const TOAST_AUTO_HIDE_DURATION = 10_000;

/**
 * OdysseyFloatingAlert component is a floating alert/toast notification that can be positioned
 * at various locations on the screen. It supports auto-hide functionality and can be dismissed
 * by the user. The component is responsive and adjusts its position based on screen size.
 *
 * For mobile case, by default it appears and the bottom center of the screen and fits the width
 * of the screen with some margin on the sides.
 *
 * For desktop case, by default it appears at the top right corner of the screen.
 *
 * Props:
 * @prop {boolean} floating - If true, the alert will float above other content.
 * @prop {('top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'bottom-center' | 'top-center')} position - The position of the alert on the screen.
 * @prop {boolean} autoHide - If true, the alert will automatically hide after a duration.
 * @prop {number} autoHideDuration - Duration in milliseconds before the alert auto-hides. Default is 10000ms.
 */
export const OdysseyFloatingAlert = (props: OdysseyFloatingAlertProps) => {
  const {
    floating = false,
    position,
    autoHideDuration = TOAST_AUTO_HIDE_DURATION,
    autoHide = false,
    isDesktop = false
  } = props;
  const positionCalculated = props.position ?? 'top-right';
  const isDownTablet = useMediaQuery(theme.breakpoints.between('desktop', 'tablet'));
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const [displayAlert, setDisplayAlert] = useState<boolean>(!!props.showAlert);

  const floatingStyle = useMemo(() => {
    if (!floating) {
      return;
    }
    const tabletHeight = isDownTablet ? '84px' : '100px';
    const navBarHeight = isDesktop ? '48px' : tabletHeight;

    if (!position && isMobile) {
      return { bottom: '24px', left: '50%', transform: 'translateX(-50%)', minWidth: '90%' };
    }

    switch (positionCalculated) {
      case 'top-left':
        return { top: `calc(24px + ${navBarHeight})`, left: '24px' };
      case 'top-right':
        return { top: `calc(24px + ${navBarHeight})`, right: '24px' };
      case 'bottom-left':
        return { bottom: '24px', left: '24px' };
      case 'bottom-right':
        return { bottom: '24px', right: '24px' };
      case 'bottom-center':
        return { bottom: '24px', left: '50%', transform: 'translateX(-50%)' };
      case 'top-center':
        return { top: `calc(24px + ${navBarHeight})`, left: '50%', transform: 'translateX(-50%)' };
      default:
        return { top: `calc(24px + ${navBarHeight})`, right: '24px' };
    }
  }, [floating, position, positionCalculated, isDesktop, isDownTablet, isMobile]);

  useEffect(() => {
    setDisplayAlert(!!props.showAlert);
  }, [props.showAlert]);
  useEffect(() => {
    if (autoHide && displayAlert) {
      const timer = setTimeout(() => {
        setDisplayAlert(false);
        props.onAlertClosed?.();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [props, displayAlert, autoHide, autoHideDuration]);

  const handleOnAlertClosed = useCallback(() => {
    props.onAlertClosed?.();
    setDisplayAlert(false);
  }, [props.onAlertClosed]);

  return (
    <NAlertStandalone
      {...props}
      onAlertClosed={handleOnAlertClosed}
      showAlert={displayAlert}
      style={{ ...props.style, ...floatingStyle, position: 'fixed', zIndex: 100 }}
    />
  );
};
